/* 
  Measurement Service
  - Handles the submission of the measurement service form
  - Adds the items to the cart or changes the measurement properties of an measurement item in the cart
  - Shows error messages
  - Resets the form and closes the modal
  - Handles the variant change
*/
class MeasurementService extends HTMLElement {
  constructor() {
    super();
    
    this.customSizeVariantId = this.dataset.customSizeVariantId;
    this.measurementVariantId = this.dataset.measurementVariantId;
    if (!this.measurementVariantId) {
      console.error("class MeasurementService: Missing measurementVariantId");
      return;
    }
    if (!this.customSizeVariantId) {
      console.error("class MeasurementService: Missing customSizeVariantId");
      return;
    }

    this.form = this.querySelector("form");
    if (!this.form) return;

    // Handle variant change
    this.handleVariantSelect();

    this.cartApi = new CartAPI();
    this.submitButton = this.form.querySelector('[type="submit"]');

    this.errorMessageWrapper = this.querySelector(
      "[data-form-error-wrapper], [data-uc-product-form-error-message-wrapper]"
    );
    this.errorMessage = this.querySelector(
      "[data-form-error-message], [data-uc-product-form-error-message]"
    );

    this.onEdit = false;

    this.form.addEventListener("submit", this.submitHandler.bind(this));

    this.modal = this.closest("modal-dialog");
    document.body.addEventListener("modalClosed", () => {
      if (!this.modal.open && this.onEdit) {
        this.resetForm();
      }
    });
  }

  resetForm() {
    this.form.reset();
    this.onEdit = false;
    this.refreshSubmitButton();
    const measurementSteps = this.querySelector("measurement-steps");
    if (measurementSteps) {
      measurementSteps.resetSteps();
    }
  }

  // Save reference to handler for cleanup
  async submitHandler(event) {
    event.preventDefault();

    // Hide previous error messages
    this.hideErrorMessage();

    try {
      let measurementProperties = {};
      // Collect input values
      this.form.querySelectorAll(".mes-input:not([data-proxy-input])").forEach((input) => {
        if (input.name) {
          const inputName = input.name
            .replace("properties[", "")
            .replace("]", "");
          if (
            this.onEdit &&
            this.measurementProperties &&
            typeof this.measurementProperties[`${inputName}`] !== "undefined"
          ) {
            delete this.measurementProperties[`${inputName}`];
          }
          switch (input.type) {
            case "checkbox":
              if (input.checked) {
                measurementProperties[`${inputName}`] = input.value;
              }
              break;
            case "radio":
              const checkedRadio = this.form.querySelector(
                `input[name="${input.name}"]:checked`
              );
              if (checkedRadio) {
                measurementProperties[`${inputName}`] = checkedRadio.value;
              }
              break;
            default:
              measurementProperties[`${inputName}`] = input.value;
              break;
          }
        }
      });
      if (this.onEdit) {
        measurementProperties = {
          ...this.measurementProperties,
          ...measurementProperties,
        };
        await this.cartApi.changeItem({
          key: this.key,
          properties: measurementProperties,
        });
      } else {
        const timestamp = Date.now();
        measurementProperties = {
          ...measurementProperties,
          _ts: timestamp,
          _measurement_service: true,
        };
        // Add to cart
        await this.cartApi.addItems([
          {
            id: this.customSizeVariantId,
            quantity: 1,
            properties: { _ts: timestamp, _custom_size: true },
          },
          {
            id: this.measurementVariantId,
            quantity: 1,
            properties: measurementProperties,
          },
        ]);
      }

      // Reset form and close modal
      this.resetForm();
      this.modal?.hide();
    } catch (error) {
      console.error("Error adding to cart:", error);
      this.showErrorMessage(
        "Could not add to cart: " + (error.message || "Unknown error")
      );
    }
  }

  showErrorMessage(message) {
    if (this.errorMessageWrapper && this.errorMessage) {
      this.errorMessageWrapper.style.display = "block";
      this.errorMessage.textContent = message;
    } else {
      alert(message);
    }
  }

  hideErrorMessage() {
    if (this.errorMessageWrapper) {
      this.errorMessageWrapper.style.display = "none";
      if (this.errorMessage) {
        this.errorMessage.textContent = "";
      }
    }
  }

  setEdit(itemJson) {
    this.onEdit = true;
    this.key = itemJson.key;
    this.measurementProperties = itemJson.properties;
    // Get all measurement inputs
    const measurementInputs = this.querySelectorAll(".mes-input");
    // Go through each input and fill values from item properties
    measurementInputs.forEach((input) => {
      const propertyName = input.name
        .replace("properties[", "")
        .replace("]", "");

      if (!itemJson.properties[propertyName]) {
        return;
      }
      switch (input.type) {
        case "checkbox":
          if (itemJson.properties[propertyName] == "on") {
            input.checked = true;
            input.dispatchEvent(new Event("change"));
          }
          break;
        case "radio":
          if (itemJson.properties[propertyName] == input.value) {
            input.checked = true;
            input.dispatchEvent(new Event("change"));
          }
          break;
        case "text":
          if (itemJson.properties["_unit_in_cm"] && input.dataset.unit) {
            input.closest("measurement-input")?.setUnit("cm");
          }
          input.value = itemJson.properties[propertyName];
          input.dispatchEvent(new Event("change"));
          break;
        default:
          input.value = itemJson.properties[propertyName];
          input.dispatchEvent(new Event("change"));
          break;
      }
    });
    this.refreshSubmitButton();
    this.dispatchEvent(new CustomEvent("mes:edit-enabled", { bubbles: true }));
  }

  refreshSubmitButton() {
    if (!this.submitButton || !this.submitButton.querySelector("span")) return;
    
    if (this.onEdit) {
      this.submitButton.querySelector("span").textContent =
        this.submitButton.dataset.updateText;
      this.submitButton.querySelector("span+span").classList.add("visually-hidden");
    } else {
      this.submitButton.querySelector("span").textContent =
        this.submitButton.dataset.addText;
      this.submitButton.querySelector("span+span").classList.remove("visually-hidden");
    }
  }

  handleVariantSelect() {
    console.log('handleVariantSelect');
    
    document.querySelectorAll('[data-main-product] variant-selects select').forEach(select => {
      console.log('select', select);
      select.addEventListener("change", (e) => {
        this.onVariantSelectChange(e.target);
      });
    });
  }
  onVariantSelectChange(select) {
    console.log('onVariantSelectChange');
    
    const value = select.value;
    const optionBlocks = document.querySelectorAll('[data-main-product] .product-form__input');
    const measurementOpenBlock = document.querySelector(
      "[data-main-product] [data-measurement-open-block]"
    );
    const addToCartBlock = document.querySelector("[data-main-product] [data-add-to-cart-block]");
    if (value == "customize") {
      measurementOpenBlock.style.display = "block";
      addToCartBlock.style.display = "none";
      optionBlocks.forEach(optionBlock => {
        if (!optionBlock.contains(select)) {
          optionBlock.classList.add('visually-hidden');
        }
      })
      const priceElement = document.querySelector('[data-main-product] .price-item--regular');
      if (priceElement) {
        const priceContainer = priceElement.closest('[role="status"]');
        // priceElement.innerHTML = this.dataset.price;
        setTimeout(() => {
          priceContainer.classList.remove('visibility-hidden');
        }, 250);
      }
    } else {
      measurementOpenBlock.style.display = "none";
      addToCartBlock.style.display = "block";
      optionBlocks.forEach(optionBlock => {
        optionBlock.classList.remove('visually-hidden');
      })
    }
  }
}

class MeasurementSteps extends HTMLElement {
  constructor() {
    super();
    this.titles = this.querySelectorAll("[data-step-title]") || [];
    this.contents = this.querySelectorAll("[data-step-content]") || [];
    this.nextBtns = this.querySelectorAll("[data-btn-next-step]") || [];
    this.prevBtns = this.querySelectorAll("[data-btn-previous-step]") || [];

    this.nextBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const stepIndex = this.getStepIndex(e.target);
        const isValid = this.validateStep(stepIndex, { silent: false });
        if (!isValid) {
          e.preventDefault();
          return;
        }
        if (e.target.type !== "submit") {
          this.goToStep(stepIndex + 1);
        }
      });
    });

    this.prevBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        this.goToStep(this.getStepIndex(e.target) - 1);
      });
    });

    this.querySelectorAll("measurement-input, height-input").forEach((input) => {
      const stepIndex = this.getStepIndex(input);
      input.addEventListener("change", (e) => {
        this.validateStep(stepIndex, { silent: true });
      });
      input.addEventListener("keyup", (e) => {
        this.validateStep(stepIndex, { silent: true });
      });
    });

    this.measurementService = this.closest("measurement-service");
    this.measurementService?.addEventListener("mes:edit-enabled", (e) => {
      this.contents.forEach((content) => {
        this.validateStep(this.getStepIndex(content), { silent: true });
      });
    });
  }


  getStepIndex(target) {
    return parseInt(target.closest("[data-step-content]").dataset.index);
  }

  validateStep(stepIndex, { silent = false }) {
    let isValid = true;
    const stepContent = this.contents[stepIndex];
    const inputs = stepContent.querySelectorAll("measurement-input, height-input");
    inputs.forEach((input) => {
      if (typeof input.validate === "function" && !input.validate({ silent: silent })) {
        isValid = false;
      }
    });
    return isValid;
  }

  resetSteps() {

    this.goToStep(0);
  }

  goToStep(stepIndex) {

    this.contents.forEach((content) => {
      if (parseInt(content.dataset.index) === stepIndex) {
        content.classList.add("active");
      } else {
        content.classList.remove("active");
      }
    });
    this.titles.forEach((title, index) => {
      const currentIndex = parseInt(title.dataset.index);
      if (currentIndex <= stepIndex) {
        title.classList.add("passed");
      } else {
        title.classList.remove("passed");
      }
    });
    const hasActiveStep = Array.from(this.contents).some((content) => content.classList.contains("active"));
    if (!hasActiveStep) {
      this.contents[0]?.classList.add("active");
      this.titles[0]?.classList.add("active");
    }
  }
}

/* 
  Measurement Guide
  - Handles the closing of the measurement guide modal
  - Closes the modal
*/
class MeasurementGuide extends HTMLElement {
  connectedCallback() {
    this.backButtons = this.querySelectorAll("[data-modal-back]");
    this.backButtons.forEach((button) => {
      button.addEventListener("click", this.closeModal.bind(this));
    });
  }

  closeModal() {
    const modal = this.closest("modal-dialog");
    modal.hide();
  }
}

// Ensure components are only defined once


if (!customElements.get("measurement-service")) {
  customElements.define("measurement-service", MeasurementService);
}

if (!customElements.get("measurement-guide")) {
  customElements.define("measurement-guide", MeasurementGuide);
}

if (!customElements.get("measurement-steps")) {
  customElements.define("measurement-steps", MeasurementSteps);
}
